import { useState } from "react";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// 24 colors organized chromatically with semantic vibes for AI context
// Each color has vibeEn for AI prompt + vibe for display
export const COLOR_VIBES = [
  // Row 1 - Warm + Pinks/Purples (12 colors)
  { id: "branco", name: "Branco", hex: "#FFFFFF", vibe: "Minimalista", vibeEn: "Clean Minimalist White", border: true },
  { id: "offwhite", name: "Creme", hex: "#F5F0E6", vibe: "Orgânico", vibeEn: "Warm Organic Cream", border: true },
  { id: "amarelo", name: "Amarelo", hex: "#FACC15", vibe: "Energético", vibeEn: "Energetic Sunny Yellow" },
  { id: "gold", name: "Dourado", hex: "#D4A853", vibe: "Luxuoso", vibeEn: "Luxury Gold Prestige" },
  { id: "laranja", name: "Laranja", hex: "#F97316", vibe: "Vibrante", vibeEn: "Vibrant Dynamic Orange" },
  { id: "coral", name: "Coral", hex: "#FF6B6B", vibe: "Acolhedor", vibeEn: "Welcoming Warm Coral" },
  { id: "vermelho", name: "Vermelho", hex: "#EF4444", vibe: "Intenso", vibeEn: "Bold Intense Red" },
  { id: "bordo", name: "Bordô", hex: "#991B1B", vibe: "Sofisticado", vibeEn: "Sophisticated Deep Burgundy" },
  { id: "rosa", name: "Rosa", hex: "#EC4899", vibe: "Criativo", vibeEn: "Creative Playful Pink" },
  { id: "magenta", name: "Magenta", hex: "#DB2777", vibe: "Ousado", vibeEn: "Bold Daring Magenta" },
  { id: "lilas", name: "Lilás", hex: "#C084FC", vibe: "Suave", vibeEn: "Soft Gentle Lavender" },
  { id: "roxo", name: "Roxo", hex: "#7C3AED", vibe: "Misterioso", vibeEn: "Mysterious Royal Purple" },
  // Row 2 - Cool + Neutrals (12 colors)
  { id: "indigo", name: "Índigo", hex: "#4F46E5", vibe: "Tech", vibeEn: "Tech-Forward Digital Indigo" },
  { id: "azul", name: "Azul", hex: "#3B82F6", vibe: "Confiável", vibeEn: "Trustworthy Corporate Blue" },
  { id: "ciano", name: "Ciano", hex: "#06B6D4", vibe: "Refrescante", vibeEn: "Refreshing Cyan Tech" },
  { id: "turquesa", name: "Turquesa", hex: "#14B8A6", vibe: "Sereno", vibeEn: "Serene Calm Turquoise" },
  { id: "verde", name: "Verde", hex: "#22C55E", vibe: "Natural", vibeEn: "Fresh Natural Green" },
  { id: "floresta", name: "Floresta", hex: "#166534", vibe: "Premium", vibeEn: "Deep Premium Forest" },
  { id: "oliva", name: "Oliva", hex: "#84CC16", vibe: "Sustentável", vibeEn: "Sustainable Eco Olive" },
  { id: "marrom", name: "Marrom", hex: "#A16207", vibe: "Rústico", vibeEn: "Rustic Artisanal Brown" },
  { id: "bege", name: "Bege", hex: "#D4A574", vibe: "Neutro", vibeEn: "Neutral Warm Beige" },
  { id: "cinza", name: "Cinza", hex: "#6B7280", vibe: "Corporativo", vibeEn: "Professional Corporate Gray" },
  { id: "navy", name: "Navy", hex: "#1E3A5F", vibe: "Autoritativo", vibeEn: "Authoritative Navy Command" },
  { id: "preto", name: "Preto", hex: "#171717", vibe: "Elegante", vibeEn: "Elegant Premium Black" },
];

// Legacy alias for backwards compatibility
const COLORS = COLOR_VIBES;

interface Step3ColorsProps {
  color: string;
  onSelect: (id: string) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
}

export function Step3Colors({ color, onSelect, onNext, onBack, canProceed }: Step3ColorsProps) {
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);

  // Get the active color info (hovered takes priority, then selected)
  const activeColorId = hoveredColor || color;
  const activeColor = COLORS.find((c) => c.id === activeColorId);

  return (
    <div className="max-w-6xl mx-auto animate-fade-in px-6 sm:px-8">
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-[26px] leading-tight sm:text-2xl md:text-3xl lg:text-4xl font-display font-semibold text-white mb-2 sm:mb-3 text-balance tracking-tight break-words">
          Escolha uma Cor
        </h2>
        <p className="text-white/80 text-sm sm:text-base lg:text-lg text-balance leading-relaxed font-sans">
          Selecione a que melhor representa a alma do seu negócio.
        </p>
      </div>

      <Card className="max-w-[600px] mx-auto py-6 px-[18px] sm:py-[30px] sm:px-6 bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.04)]">
        {/* 24 Color Grid: 4 cols mobile, 6 cols desktop */}
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 sm:gap-2.5 place-items-center">
          {COLORS.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              onMouseEnter={() => setHoveredColor(c.id)}
              onMouseLeave={() => setHoveredColor(null)}
              className={cn(
                "rounded-full transition-all duration-200 ease-out relative w-[52px] h-[52px] sm:w-[60px] sm:h-[60px] active:scale-95 hover:-translate-y-0.5",
                c.border && "border-2 border-muted",
                color === c.id
                  ? "ring-2 ring-primary ring-offset-2 ring-offset-white shadow-md scale-105"
                  : "shadow-sm hover:shadow-md"
              )}
              style={{ backgroundColor: c.hex }}
              title={`${c.name} - ${c.vibe}`}
            >
              {color === c.id && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Check className={cn(
                    "w-5 h-5",
                    ["branco", "offwhite", "amarelo", "bege", "oliva"].includes(c.id) ? "text-gray-800" : "text-white"
                  )} />
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Tooltip showing color name and vibe */}
        <div className="h-6 mt-4 flex items-center justify-center">
          {activeColor && (
            <p className="text-sm text-muted-foreground animate-fade-in">
              <span className="font-medium text-foreground">{activeColor.name}</span>
              <span className="mx-2">·</span>
              <span>{activeColor.vibe}</span>
            </p>
          )}
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/20">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Button onClick={onNext} disabled={!canProceed} className="bg-white text-primary hover:bg-white/90 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          Próximo Passo
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default Step3Colors;
